import classNames from "classnames";
import classes from "./InformationContainer.module.less";
import InformationCard from "./InformationCard";
import { MaterialInput } from "~/common/components/materialInput/MaterialInput";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import UserInfoModal from "./UserInfoModal";
import { Button } from "~/common/components/Button/Button";
import { Link } from "~/common/components/Link";
import { type Dispatch, forwardRef, type SetStateAction } from "react";
import { useSalutationOptions } from "~/ScanDMC/components/ContactFormOverlay/dmcContactFormUtils";
import { type DataObject } from "~/pages/B2CRequestPage";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { CART_STEP_2 } from "~/utils/url/staticUrls";
import { updateUserInfo } from "../api/updateUserInfo";
import { transformUserInfoData } from "../utils";
import { SALUTATION_CODES, type UserInfoData } from "../types";
import { t } from "i18next";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { AlertTitle } from "@mui/material";
import { Alert } from "~/common/m3/Alert/Alert";
import { Checkboxes } from "./Checkboxes";
import { userIsLoggedIn } from "~/utils/roles";
import { submitGuestCheckoutForm } from "../../B2CCart/components/GuestCheckoutDialog/api/guestCheckoutSubmitForm";
import { Controller } from "react-hook-form";

interface InformationContainerProps {
  className?: string;
  onButtonClick: () => void;
  dataObject: DataObject;
  setDataObject: Dispatch<SetStateAction<DataObject>>;
  hasAllUserInfo: boolean;
  marketingConsent: boolean;
}

const HIDDEN_SALUTATIONS = [SALUTATION_CODES.DIVERSE, SALUTATION_CODES.PREFER_NOT_TO_SAY];

const InformationContainer = forwardRef<HTMLDivElement, InformationContainerProps>(
  ({ className, onButtonClick, dataObject, setDataObject, hasAllUserInfo, marketingConsent }, ref) => {
    const { val: userInfoModalOpen, setFalse: closeUserInfoModal, setTrue: openUserInfoModal } = useBooleanState(false);

    const isDesktop = useIsMinDesktopSize();
    const isTablet = useIsMinTabletSize();

    const salutationOptions = useSalutationOptions();

    const handleUserSalutation = (code: string) => {
      if (HIDDEN_SALUTATIONS.includes(code as SALUTATION_CODES)) {
        return "";
      } else {
        return salutationOptions.find((option) => option.value === dataObject.userDetails.salutationCode)?.label;
      }
    };

    const userSalutation = handleUserSalutation(dataObject.userDetails.salutationCode);

    const handleFormSubmit = (data: UserInfoData) => {
      setDataObject({ ...dataObject, userDetails: data });
      if (data.updateUser) {
        if (userIsLoggedIn()) {
          updateUserInfo(transformUserInfoData(data)).then(() => closeUserInfoModal());
        } else {
          submitGuestCheckoutForm({
            ...transformUserInfoData(data),
            companyName: data.company,
            email: data.email,
            line2: data.additionalLine1 ?? "",
          }).then(() => closeUserInfoModal());
        }
      } else {
        closeUserInfoModal();
      }
    };

    const goToStep2 = () => redirectToUrl(CART_STEP_2);

    const errorMessage = !hasAllUserInfo && (
      <Alert severity="error">
        <AlertTitle>{t("request.missingInfoErrorTitle")}</AlertTitle>
        {t("request.missingInfoErrorMessage")}
      </Alert>
    );

    return (
      <div className={classNames(classes.root, className)}>
        <InformationCard className={classes.card} title={t("request.informationLabel")}>
          <MaterialInput
            value={dataObject.requestNumber}
            onChange={(e) => setDataObject({ ...dataObject, requestNumber: e.target.value })}
            fullWidth={false}
            placeholder={t("request.number")}
          />
        </InformationCard>
        <InformationCard
          error={!hasAllUserInfo}
          className={classes.card}
          title={t("request.userInformation")}
          editAction={openUserInfoModal}
        >
          <div className={classes.cardContainer}>
            <div>
              <div>
                <span className={classes.label}>{t("request.information.name")}</span>
              </div>
              <div>
                <b>{dataObject.userDetails.company}</b>
              </div>
              <div>{`${userSalutation} ${dataObject.userDetails.firstName} ${dataObject.userDetails.lastName}`}</div>
            </div>

            <div className={classes.flexAuto}>
              <div>
                <span className={classes.label}>{t("page.completeRegistration.address")}</span>
              </div>
              {/*
              Line 1 contains house number and street
              Additional address line (displayed after Line1)
              Line2 contains the city, region and postalcode
              */}
              <div>{`${dataObject.userDetails.line1}`}</div>
              {dataObject.userDetails.additionalLine1 && <div>{`${dataObject.userDetails.additionalLine1}`}</div>}
              <div>{`${dataObject.userDetails.line2}`}</div>

              <div>{dataObject.userDetails.country && t(`country.${dataObject.userDetails.country}translation`)}</div>
            </div>
            <div>
              <div className={classes.contactContainer}>
                <div className={classes.label}>
                  <span>{t("request.contact")}</span>
                </div>

                <div>{dataObject.userDetails.phone}</div>
                <div>{dataObject.userDetails.email}</div>
                <div className={dataObject.salesPartnerData.email && classes.bottomMargin} />
              </div>
            </div>
          </div>
        </InformationCard>

        {!isTablet && errorMessage}

        <InformationCard className={classes.card} title={t("request.salesPartnerInformation")} editAction={goToStep2}>
          <div className={classes.cardContainer}>
            <div>
              <div className={classes.label}>
                <span>{t("request.information.name")}</span>
              </div>
              <div className={classes.bottomMargin}>
                <b>{dataObject.salesPartnerData.name}</b>
              </div>
            </div>

            <div className={classes.flexAuto}>
              <div className={classes.label}>
                <span>{t("page.completeRegistration.address")}</span>
              </div>
              <div>{`${dataObject.salesPartnerData.line1}`}</div>
              <div>{`${dataObject.salesPartnerData.line2 ?? ""}`}</div>
              <div>{t(`country.${dataObject.salesPartnerData.country}translation`)}</div>
            </div>

            {(dataObject.salesPartnerData.phone || dataObject.salesPartnerData.email) && (
              <div className={classes.contactContainer}>
                <div>
                  <span className={classes.label}>{t("request.contact")}</span>
                </div>

                <div className={!dataObject.salesPartnerData.phone && classes.bottomMargin}>
                  {dataObject.salesPartnerData.phone}
                </div>

                <div>{dataObject.salesPartnerData.fax}</div>

                <Link href={`mailto:${dataObject.salesPartnerData.email}`}>{dataObject.salesPartnerData.email}</Link>
              </div>
            )}
          </div>
        </InformationCard>

        <div ref={ref} className={classNames(classes.checkBox, !hasAllUserInfo && !isDesktop && classes.reverse)}>
          <div className={classes.checkboxContent}>
            <Controller
              name="tcAgreement"
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Checkboxes.TermsConditions
                    checked={field.value}
                    error={!!error}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />

                  {error && <Alert severity="error">{t("request.tcAgree")}</Alert>}
                </>
              )}
            />
            <Controller
              name="ppAgreement"
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Checkboxes.PrivacyPolicy
                    checked={field.value}
                    error={!!error}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  {error && <Alert severity="error">{t("request.privacyAgree")}</Alert>}
                </>
              )}
            />
            {!marketingConsent && (
              <Controller
                name="userAgreement"
                render={({ field, fieldState: { error } }) => (
                  <Checkboxes.MarketingConsent
                    checked={field.value}
                    error={!!error}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            )}

            <div className={classes.mandatoryMessage}>{t("mandatory")}</div>
          </div>
          {isTablet && errorMessage}
        </div>
        <div className={classes.footer}>
          <Button disabled={!hasAllUserInfo} onClick={onButtonClick} fullWidth>
            {t("page.cart.b2c.process.step.3")}
          </Button>
        </div>

        {userInfoModalOpen && (
          <UserInfoModal
            values={dataObject.userDetails}
            formSubmit={handleFormSubmit}
            closeModal={closeUserInfoModal}
          />
        )}
      </div>
    );
  },
);

export default InformationContainer;
